import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';


const FitnessBaselineAssessment = ({ clientId, data, onChange, onComplete }) => {
  const [formData, setFormData] = useState({
    // Body Composition
    weight: '',
    height: '',
    bodyFat: '',
    muscleMass: '',
    waist: '',
    chest: '',
    hips: '',
    arms: '',
    thighs: '',
    // Strength Tests
    pushups: '',
    plankTime: '',
    squats: '',
    deadliftMax: '',
    benchPressMax: '',
    // Cardiovascular
    restingHeartRate: '',
    vo2Max: '',
    mileTime: '',
    stepTest: '',
    // Flexibility
    sitAndReach: '',
    shoulderFlexibility: '',
    hipFlexibility: '',
    // Photos
    frontPhoto: null,
    sidePhoto: null,
    backPhoto: null,
    notes: '',
    ...data
  });

  useEffect(() => {
    onChange(formData);
    checkCompletion();
  }, [formData]);

  const checkCompletion = () => {
    const required = ['weight', 'height', 'restingHeartRate'];
    const isComplete = required?.every(field => formData?.[field]?.toString()?.trim());
    onComplete(isComplete);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (photoType, e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, [photoType]: event?.target?.result }));
      };
      reader?.readAsDataURL(file);
    }
  };

  const calculateBMI = () => {
    const weight = parseFloat(formData?.weight);
    const height = parseFloat(formData?.height) / 100; // convert cm to m
    if (weight && height) {
      return (weight / (height * height))?.toFixed(1);
    }
    return null;
  };

  const bmi = calculateBMI();

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Fitness Baseline Assessment</h2>
        <p className="text-muted-foreground">Comprehensive measurements to track progress over time</p>
      </div>
      {/* Body Composition */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Scale" size={20} className="text-primary" />
          Body Composition
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Input
              label="Weight (kg)"
              name="weight"
              type="number"
              step="0.1"
              value={formData?.weight}
              onChange={handleInputChange}
              placeholder="70.5"
              required
            />
          </div>
          <div>
            <Input
              label="Height (cm)"
              name="height"
              type="number"
              value={formData?.height}
              onChange={handleInputChange}
              placeholder="175"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">BMI</label>
            <div className="h-10 px-4 py-2 rounded-lg border border-border bg-muted flex items-center">
              <span className="text-lg font-semibold text-primary">
                {bmi || '--'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Body Fat Percentage (%)"
            name="bodyFat"
            type="number"
            step="0.1"
            value={formData?.bodyFat}
            onChange={handleInputChange}
            placeholder="20.5"
          />
          <Input
            label="Muscle Mass (kg)"
            name="muscleMass"
            type="number"
            step="0.1"
            value={formData?.muscleMass}
            onChange={handleInputChange}
            placeholder="45.0"
          />
        </div>
      </div>
      {/* Body Measurements */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Ruler" size={20} className="text-primary" />
          Body Measurements (cm)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Input
            label="Waist"
            name="waist"
            type="number"
            step="0.1"
            value={formData?.waist}
            onChange={handleInputChange}
            placeholder="80"
          />
          <Input
            label="Chest"
            name="chest"
            type="number"
            step="0.1"
            value={formData?.chest}
            onChange={handleInputChange}
            placeholder="95"
          />
          <Input
            label="Hips"
            name="hips"
            type="number"
            step="0.1"
            value={formData?.hips}
            onChange={handleInputChange}
            placeholder="100"
          />
          <Input
            label="Arms"
            name="arms"
            type="number"
            step="0.1"
            value={formData?.arms}
            onChange={handleInputChange}
            placeholder="30"
          />
          <Input
            label="Thighs"
            name="thighs"
            type="number"
            step="0.1"
            value={formData?.thighs}
            onChange={handleInputChange}
            placeholder="55"
          />
        </div>
      </div>
      {/* Strength Tests */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Dumbbell" size={20} className="text-primary" />
          Strength Assessment
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Push-ups (max reps)"
            name="pushups"
            type="number"
            value={formData?.pushups}
            onChange={handleInputChange}
            placeholder="20"
            description="Maximum consecutive push-ups"
          />
          <Input
            label="Plank Hold (seconds)"
            name="plankTime"
            type="number"
            value={formData?.plankTime}
            onChange={handleInputChange}
            placeholder="60"
            description="Maximum plank hold time"
          />
          <Input
            label="Bodyweight Squats (max reps)"
            name="squats"
            type="number"
            value={formData?.squats}
            onChange={handleInputChange}
            placeholder="30"
            description="Maximum consecutive squats"
          />
          <Input
            label="Deadlift 1RM (kg)"
            name="deadliftMax"
            type="number"
            step="0.5"
            value={formData?.deadliftMax}
            onChange={handleInputChange}
            placeholder="100"
            description="One rep maximum (if applicable)"
          />
        </div>
      </div>
      {/* Cardiovascular Assessment */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Heart" size={20} className="text-primary" />
          Cardiovascular Fitness
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Resting Heart Rate (bpm)"
            name="restingHeartRate"
            type="number"
            value={formData?.restingHeartRate}
            onChange={handleInputChange}
            placeholder="65"
            required
            description="Measured after 5 minutes of rest"
          />
          <Input
            label="VO2 Max (ml/kg/min)"
            name="vo2Max"
            type="number"
            step="0.1"
            value={formData?.vo2Max}
            onChange={handleInputChange}
            placeholder="45.0"
            description="If available from testing"
          />
          <Input
            label="1 Mile Run Time (minutes)"
            name="mileTime"
            type="number"
            step="0.1"
            value={formData?.mileTime}
            onChange={handleInputChange}
            placeholder="8.5"
            description="Best mile time"
          />
          <Input
            label="3-Minute Step Test (bpm)"
            name="stepTest"
            type="number"
            value={formData?.stepTest}
            onChange={handleInputChange}
            placeholder="120"
            description="Heart rate after 3-min step test"
          />
        </div>
      </div>
      {/* Flexibility Assessment */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Wind" size={20} className="text-primary" />
          Flexibility & Mobility
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Sit and Reach (cm)
            </label>
            <input
              type="number"
              name="sitAndReach"
              step="0.5"
              value={formData?.sitAndReach}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
              placeholder="25"
            />
            <p className="text-xs text-muted-foreground mt-1">Hamstring flexibility test</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Shoulder Flexibility
            </label>
            <select
              name="shoulderFlexibility"
              value={formData?.shoulderFlexibility}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select rating...</option>
              <option value="poor">Poor</option>
              <option value="fair">Fair</option>
              <option value="good">Good</option>
              <option value="excellent">Excellent</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Shoulder mobility assessment</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Hip Flexibility
            </label>
            <select
              name="hipFlexibility"
              value={formData?.hipFlexibility}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            >
              <option value="">Select rating...</option>
              <option value="poor">Poor</option>
              <option value="fair">Fair</option>
              <option value="good">Good</option>
              <option value="excellent">Excellent</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Hip mobility assessment</p>
          </div>
        </div>
      </div>
      {/* Progress Photos */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Camera" size={20} className="text-primary" />
          Progress Photos
        </h3>
        <p className="text-sm text-muted-foreground">Upload baseline photos for progress tracking</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['frontPhoto', 'sidePhoto', 'backPhoto']?.map((photoType) => (
            <div key={photoType} className="space-y-2">
              <label className="block text-sm font-medium text-foreground capitalize">
                {photoType?.replace('Photo', ' View')}
              </label>
              <div className="relative">
                {formData?.[photoType] ? (
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden border-2 border-primary">
                    <img
                      src={formData?.[photoType]}
                      alt={`${photoType} view`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, [photoType]: null }))}
                      className="absolute top-2 right-2 w-8 h-8 bg-destructive rounded-full flex items-center justify-center hover:bg-destructive/90"
                    >
                      <Icon name="X" size={16} color="white" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center aspect-[3/4] border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                    <Icon name="Upload" size={32} className="text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Upload Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(photoType, e)}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Additional Notes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          Assessment Notes
        </h3>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Observations
          </label>
          <textarea
            name="notes"
            value={formData?.notes}
            onChange={handleInputChange}
            className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-ring"
            rows={4}
            placeholder="Record any additional observations, movement patterns, or areas of concern..."
          />
        </div>
      </div>
      {/* Quick Reference Card */}
      <div className="bg-secondary/10 border border-secondary rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-secondary mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-1">Assessment Tips</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Take measurements at the same time of day for consistency</li>
              <li>• Ensure proper warm-up before strength and flexibility tests</li>
              <li>• Photos should be taken in consistent lighting and clothing</li>
              <li>• Record any factors that may affect results (illness, fatigue, etc.)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitnessBaselineAssessment;